/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { useText } from '@adobe-commerce/elsie/i18n';
import { Button } from '@adobe-commerce/elsie/components';
import { Close, CheckWithCircle, WarningFilled } from '@adobe-commerce/elsie/icons';
import { formatFileSize } from '@/quote-management/utils/fileUtils';
import './AttachedFilesList.css';

export interface AttachedFile {
  key: string;
  name: string;
  size: number;
  status: 'uploading' | 'success' | 'error';
  error?: string;
}

export interface AttachedFilesListProps {
  files: AttachedFile[];
  onRemove: (key: string) => void;
  disabled?: boolean;
}

export const AttachedFilesList: FunctionComponent<AttachedFilesListProps> = ({
  files,
  onRemove,
  disabled = false,
}) => {
  const dictionary = useText({
    removeFile: 'NegotiableQuote.Manage.removeFile',
    uploading: 'NegotiableQuote.Manage.uploading',
    uploadSuccess: 'NegotiableQuote.Manage.uploadSuccess',
    uploadError: 'NegotiableQuote.Manage.uploadError',
  });

  if (!files || files.length === 0) {
    return null;
  }

  return (
    <div className="attached-files-list" data-testid="attached-files-list">
      {files.map((file) => {
        const isUploading = file.status === 'uploading';
        const isError = file.status === 'error';
        const isSuccess = file.status === 'success';
        const canRemove = !disabled && !isUploading;

        return (
          <div
            key={file.key}
            className={`attached-files-list__item attached-files-list__item--${file.status}`}
            data-testid={`attached-file-${file.key}`}
          >
            <div className="attached-files-list__item-main">
              <div className="attached-files-list__item-icon">
                {isUploading && (
                  <div
                    className="attached-files-list__spinner"
                    data-testid="file-uploading-spinner"
                    aria-label={dictionary.uploading}
                  />
                )}
                {isSuccess && (
                  <CheckWithCircle
                    data-testid="file-success-icon"
                    aria-label={dictionary.uploadSuccess}
                    className="attached-files-list__success-icon"
                  />
                )}
                {isError && (
                  <WarningFilled
                    data-testid="file-error-icon"
                    aria-label={dictionary.uploadError}
                    color="var(--color-error)"
                    className="attached-files-list__error-icon"
                  />
                )}
              </div>

              <div className="attached-files-list__item-info">
                <div className="attached-files-list__item-name" data-testid="file-name">
                  {file.name}
                </div>
                <div className="attached-files-list__item-size" data-testid="file-size">
                  {formatFileSize(file.size)}
                  {isUploading && ` - ${dictionary.uploading}`}
                </div>
              </div>

              <Button
                variant="tertiary"
                onClick={() => onRemove(file.key)}
                disabled={!canRemove}
                type="button"
                aria-label={`${dictionary.removeFile}: ${file.name}`}
                data-testid={`remove-file-${file.key}`}
                className="attached-files-list__remove-button"
              >
                <Close />
              </Button>
            </div>

            {isError && file.error && (
              <div
                className="attached-files-list__item-error"
                data-testid="file-error-message"
              >
                {file.error}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

